// components/ChatbotAPISettings.tsx
import React, { useState, useEffect } from 'react';
import { Key, AlertCircle, Check, X, Loader2, TestTube } from 'lucide-react';

interface APIProvider {
  name: string;
  provider: string;
  model: string;
  apiKey: string;
  enabled: boolean;
}

export const ChatbotAPISettings: React.FC = () => {
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testResults, setTestResults] = useState<{ [key: string]: { success: boolean; message: string } }>({});
  const [testing, setTesting] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch('/api-settings.json');
      const data = await response.json();
      setSettings(data);
      
      // Sprawdź czy są zapisane ustawienia w localStorage
      const savedSettings = localStorage.getItem('api_settings');
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        setSettings(parsed);
      }
    } catch (error) {
      console.error('Error loading API settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      // Zapisz w localStorage
      localStorage.setItem('api_settings', JSON.stringify(settings));
      
      // Wyemituj event dla innych komponentów
      window.dispatchEvent(new CustomEvent('api-settings-updated', { detail: settings }));
      
      // Pokaż sukces
      alert('Ustawienia API zostały zapisane!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Błąd podczas zapisywania ustawień');
    } finally {
      setSaving(false);
    }
  };

  const testAPIKey = async (provider: string) => {
    setTesting(provider);
    setTestResults({ ...testResults, [provider]: { success: false, message: 'Testowanie...' } });

    try {
      let apiKey = '';
      let model = '';
      
      if (provider === 'anthropic') {
        apiKey = settings.anthropic.apiKey;
        model = settings.anthropic.model;
      } else if (provider === 'openai') {
        apiKey = settings.openai.apiKey;
        model = settings.openai.model;
      } else if (provider === 'google') {
        apiKey = settings.google.apiKey;
        model = settings.google.model;
      }

      if (!apiKey) {
        setTestResults({ 
          ...testResults, 
          [provider]: { success: false, message: 'Brak klucza API' } 
        });
        setTesting(null);
        return;
      }

      // Test dla Anthropic
      if (provider === 'anthropic') {
        const response = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey,
            'anthropic-version': '2023-06-01'
          },
          body: JSON.stringify({
            model: model,
            messages: [{ role: 'user', content: 'Say "Test OK" in 3 words.' }],
            max_tokens: 10
          })
        });

        if (response.ok) {
          setTestResults({ 
            ...testResults, 
            [provider]: { success: true, message: 'Połączenie działa!' } 
          });
        } else {
          const error = await response.text();
          setTestResults({ 
            ...testResults, 
            [provider]: { success: false, message: `Błąd: ${response.status}` } 
          });
        }
      }
      
      // Test dla OpenAI
      else if (provider === 'openai') {
        const response = await fetch('https://api.openai.com/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`
          },
          body: JSON.stringify({
            model: model,
            messages: [{ role: 'user', content: 'Say "Test OK"' }],
            max_tokens: 10
          })
        });

        if (response.ok) {
          setTestResults({ 
            ...testResults, 
            [provider]: { success: true, message: 'Połączenie działa!' } 
          });
        } else {
          setTestResults({ 
            ...testResults, 
            [provider]: { success: false, message: `Błąd: ${response.status}` } 
          });
        }
      }
      
    } catch (error: any) {
      setTestResults({ 
        ...testResults, 
        [provider]: { success: false, message: error.message } 
      });
    } finally {
      setTesting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-zinc-800 rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Key className="w-5 h-5 text-orange-500" />
          Konfiguracja API dla Chatbota
        </h2>

        <div className="mb-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
            <div className="text-sm text-gray-300">
              <p className="font-semibold mb-1">Ważne!</p>
              <p>Chatbot wymaga klucza API do działania. Możesz używać:</p>
              <ul className="list-disc list-inside mt-1">
                <li>Claude (Anthropic) - zalecane dla chatbota</li>
                <li>GPT-4 (OpenAI) - alternatywa</li>
                <li>Gemini (Google) - darmowy limit</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Anthropic/Claude */}
        <div className="space-y-4">
          <div className="border border-zinc-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-100">Anthropic (Claude)</h3>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings?.anthropic?.enabled || false}
                  onChange={(e) => setSettings({
                    ...settings,
                    anthropic: { ...settings.anthropic, enabled: e.target.checked }
                  })}
                  className="w-4 h-4 text-orange-600"
                />
                <span className="text-sm">Włączony</span>
              </label>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Klucz API
                </label>
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={settings?.anthropic?.apiKey || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      anthropic: { ...settings.anthropic, apiKey: e.target.value }
                    })}
                    placeholder="sk-ant-api03-..."
                    className="flex-1 px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white"
                  />
                  <button
                    onClick={() => testAPIKey('anthropic')}
                    disabled={testing === 'anthropic'}
                    className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    {testing === 'anthropic' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <TestTube className="w-4 h-4" />
                    )}
                    Test
                  </button>
                </div>
                {testResults.anthropic && (
                  <div className={`mt-2 text-sm flex items-center gap-1 ${testResults.anthropic.success ? 'text-green-400' : 'text-red-400'}`}>
                    {testResults.anthropic.success ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    {testResults.anthropic.message}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Model
                </label>
                <select
                  value={settings?.anthropic?.model || 'claude-3-5-sonnet-20241022'}
                  onChange={(e) => setSettings({
                    ...settings,
                    anthropic: { ...settings.anthropic, model: e.target.value }
                  })}
                  className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white"
                >
                  <option value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet (Zalecany)</option>
                  <option value="claude-3-opus-20240229">Claude 3 Opus</option>
                  <option value="claude-3-haiku-20240307">Claude 3 Haiku (Szybki)</option>
                </select>
              </div>
            </div>
          </div>

          {/* OpenAI */}
          <div className="border border-zinc-700 rounded-lg p-4 opacity-50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-100">OpenAI (GPT)</h3>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings?.openai?.enabled || false}
                  onChange={(e) => setSettings({
                    ...settings,
                    openai: { ...settings.openai, enabled: e.target.checked }
                  })}
                  className="w-4 h-4 text-orange-600"
                />
                <span className="text-sm">Włączony</span>
              </label>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Klucz API
                </label>
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={settings?.openai?.apiKey || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      openai: { ...settings.openai, apiKey: e.target.value }
                    })}
                    placeholder="sk-..."
                    className="flex-1 px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white"
                  />
                  <button
                    onClick={() => testAPIKey('openai')}
                    disabled={testing === 'openai'}
                    className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    {testing === 'openai' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <TestTube className="w-4 h-4" />
                    )}
                    Test
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Przyciski akcji */}
        <div className="mt-6 flex gap-3">
          <button
            onClick={saveSettings}
            disabled={saving}
            className="flex-1 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Zapisywanie...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Zapisz ustawienia
              </>
            )}
          </button>
        </div>
      </div>

      {/* Instrukcje */}
      <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-6">
        <h3 className="font-semibold mb-2">📚 Jak uzyskać klucz API?</h3>
        <div className="space-y-2 text-sm text-gray-300">
          <div>
            <strong>Anthropic (Claude):</strong>
            <ol className="list-decimal list-inside ml-4 mt-1">
              <li>Przejdź na <a href="https://console.anthropic.com" target="_blank" className="text-orange-400 hover:underline">console.anthropic.com</a></li>
              <li>Zaloguj się lub utwórz konto</li>
              <li>Przejdź do API Keys</li>
              <li>Kliknij "Create Key"</li>
              <li>Skopiuj klucz i wklej tutaj</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};
